/**
 * Default RunDreamSeams — the real propose + applyDeltas composition. This is
 * the single place the engine wires its own primitives
 * (`propose` + the four apply gates + `applyVerb`) into the seam shape
 * `runDream` consumes, so EVERY edge (the CLI live path, the gateway timer) calls
 * `runDream(input, deps)` with no duplicated gate logic.
 *
 * Why this exists as a defaulted seam rather than inlined in runDream:
 * `runDream(input, deps, seams)` shipped with the seams injected so its
 * orchestration was unit-provable against fakes. The edge contract is
 * 2-arg (`runDream(input, deps)`); defaulting the third param to THIS composer
 * restores that contract while keeping the injected-seam tests intact.
 *
 * Faithful to the apply pipeline order (apply.ts): per delta —
 *   forcePolicyQuarantine → write-path allowlist (B1, may THROW BatchAbort) →
 *   evidence-membership → idempotency → origin-tier (may THROW
 *   BatchAbort) → applyVerb → FileChange[].
 * The index file `applyVerb` bundles is hoisted into a single merge-safe
 * `IndexEdit` so commitWithRebase rebuilds it on the live tip (never stale-base
 * clobber).
 */
import type { NormalizedDreamInput } from "../types";
import type { RunDreamSeams } from "./index";
import type { DreamDeps, ProposalDelta, DigestEntry, FileChange } from "./types";
import type { IndexEdit } from "./rebase";
import { propose } from "./propose";
import { buildSystemPrompt, buildUserPrompt } from "./prompt";
import { loadMemoryContext, type MemoryContext } from "./memory-context";
import {
  applyVerb,
  checkEvidenceMembership,
  checkIdempotency,
  checkOriginTier,
  checkPathAllowed,
  forcePolicyQuarantine,
  BatchAbort,
  type ApplyCtx,
} from "./apply";

const INDEX_PATH = "memory/MEMORY.md";

/** The conv_ ids consumed/recovered this cycle — the evidence-membership set. */
function harvestedIds(input: NormalizedDreamInput): Set<string> {
  return new Set(input.conversations.map((c) => c.conversationId).filter((id): id is string => typeof id === "string"));
}

/**
 * The memory path(s) whose index line this verb RETIRES — the removal channel the
 * merge-safe IndexEdit needs so commitWithRebase reconciles (prepend − remove)
 * against the live tip. `new` adds only; `supersede` retires the
 * oldPath; `retract` retires the path. Derived from the delta (NOT re-diffed from
 * applyVerb's index blob) so a pure-removal retract — whose blob's first line is
 * an UNRELATED existing entry — never mis-feeds that line as our prepend.
 */
function retiredIndexPaths(delta: ProposalDelta): string[] {
  if (delta.verb === "supersede") return [delta.oldPath];
  if (delta.verb === "retract") return [delta.path];
  return [];
}

/**
 * The newest-first index line this verb PREPENDS, or "" if it only de-indexes.
 * `new`/`supersede` prepend a line for the (new) file; `retract` prepends nothing.
 * Read from applyVerb's freshly-built index blob (the first `- \`memory/` line is
 * the one we just prepended) — but ONLY for verbs that actually add a line, so a
 * retract's blob (whose first line is a survivor) is not mistaken for a prepend.
 */
function prependedIndexLine(delta: ProposalDelta, idx: FileChange): string {
  if (delta.verb !== "new" && delta.verb !== "supersede") return "";
  const lines = idx.content.split("\n");
  const firstLineIdx = lines.findIndex((l) => l.startsWith("- `memory/"));
  return firstLineIdx === -1 ? "" : lines[firstLineIdx];
}

/**
 * Split the index FileChange `applyVerb` bundles out into a merge-safe IndexEdit
 * carrying BOTH a prepend channel (the newest-first line we added) and a removal
 * channel (the paths this verb retired). commitWithRebase reconciles both onto
 * each attempt's live MEMORY.md (superseded/retracted file
 * removed from the index), so we never replay a stale-base index NOR leave a
 * stale/duplicate line. The non-index changes pass through unchanged.
 */
function hoistIndex(delta: ProposalDelta, changes: FileChange[], prior?: IndexEdit): { rest: FileChange[]; indexEdit?: IndexEdit } {
  const idx = changes.find((c) => c.path === INDEX_PATH);
  const rest = changes.filter((c) => c.path !== INDEX_PATH);
  if (!idx) return { rest, indexEdit: prior };
  const header = headerOf(idx);
  const prependLine = prependedIndexLine(delta, idx);
  const removePaths = retiredIndexPaths(delta);
  // Merge multiple deltas: keep the FIRST header; concat prepends newest-first; union removals.
  const merged: IndexEdit = prior
    ? {
        path: INDEX_PATH,
        header: prior.header,
        prependLine: [prependLine, prior.prependLine].filter(Boolean).join("\n"),
        removePaths: [...(prior.removePaths ?? []), ...removePaths],
      }
    : { path: INDEX_PATH, header, prependLine, ...(removePaths.length ? { removePaths } : {}) };
  return { rest, indexEdit: merged };
}

/** The header block (everything above the first index line) of a built index blob. */
function headerOf(idx: FileChange): string {
  const lines = idx.content.split("\n");
  const firstLineIdx = lines.findIndex((l) => l.startsWith("- `memory/"));
  return firstLineIdx === -1 ? idx.content.replace(/\n+$/, "") + "\n" : lines.slice(0, firstLineIdx).join("\n") + "\n";
}

export interface DefaultDreamSeamsOpts {
  /** Injected backoff for tests (propose retries). Production uses the engine default. */
  sleep?: (attempt: number) => Promise<void>;
}

/**
 * Build the real RunDreamSeams. Two per-instance buffers carry state across the
 * one propose→applyDeltas pass of a single runDream call (the default param
 * builds a FRESH seams per runDream, so this never leaks between runs):
 *  - `pendingRejected` — propose-time per-delta rejections, surfaced in the
 *    applyDeltas digest.
 *  - `memoCache` — the loaded MemoryContext (`listMemory()` + read MEMORY.md
 *    against the live brain). propose ALWAYS precedes applyDeltas within one run
 *    and both need the SAME pre-write memory snapshot, so we load it ONCE per
 *    brain instead of twice (avoids a doubled remote read on the hot path). Keyed
 *    on the brain object so a defensive applyDeltas-without-propose still loads.
 */
export function defaultDreamSeams(opts: DefaultDreamSeamsOpts = {}): RunDreamSeams {
  let pendingRejected: DigestEntry[] = [];
  const memoCache = new WeakMap<DreamDeps["brain"], Promise<MemoryContext>>();
  const memory = (brain: DreamDeps["brain"]): Promise<MemoryContext> => {
    let cached = memoCache.get(brain);
    if (!cached) {
      cached = loadMemoryContext(brain);
      memoCache.set(brain, cached);
    }
    return cached;
  };

  return {
    async propose(input: NormalizedDreamInput, deps: DreamDeps) {
      const mem = await memory(deps.brain);
      const system = buildSystemPrompt();
      const user = buildUserPrompt(input, mem.files);
      const res = await propose(deps.model, system, user, {
        ...(opts.sleep ? { sleep: opts.sleep } : {}),
        // The configured model NAME for the cost digest. Without
        // this, propose falls back to its default and every cost line mis-reports
        // the model an operator actually configured (e.g. gpt-5-mini → "gpt-5.4").
        ...(deps.modelName ? { model: deps.modelName } : {}),
      });
      pendingRejected = res.rejected; // surfaced by applyDeltas (same invocation)
      const cost = { model: res.cost.model, inputTokens: res.cost.inputTokens, outputTokens: res.cost.outputTokens, ms: res.cost.ms };
      if (res.failure) return { deltas: null, cost, failure: res.failure.detail };
      return { deltas: res.deltas, cost };
    },

    async applyDeltas(deltas: ProposalDelta[], input: NormalizedDreamInput, deps: DreamDeps) {
      const mem = await memory(deps.brain); // reuses propose's snapshot (same run, same pre-write memory)
      const harvested = harvestedIds(input);
      const ctx: ApplyCtx = { readFile: (p) => deps.brain.readFile(p), at: deps.clock() };
      const lookup = (path: string): Promise<typeof mem.files[0] | null> => Promise.resolve(mem.files.find((f) => f.path === path) ?? null);

      const digest: DigestEntry[] = [...pendingRejected];
      let changes: FileChange[] = [];
      let indexEdit: IndexEdit | undefined;

      try {
        for (const raw of deltas) {
          const delta = forcePolicyQuarantine(raw); // may rewrite new/supersede → quarantine

          // B1 (SECURITY) — fail-closed write-path allowlist BEFORE applyVerb. An escaping
          // new/flag is dropped (rejected); an escaping supersede/retract THROWS BatchAbort
          // (caught below → whole batch aborts). Runs before origin-tier because origin-tier
          // can't see non-memory/ paths (lookup null → worker → editable → would pass).
          const path = checkPathAllowed(delta);
          if (!path.ok) { digest.push(path.rejected); continue; }

          const ev = checkEvidenceMembership(delta, harvested);
          if (!ev.ok) { digest.push(ev.rejected); continue; }

          const idem = checkIdempotency(delta, mem.files);
          if (!idem.ok) { digest.push(idem.skipped); continue; }

          const origin = await checkOriginTier(delta, lookup); // may THROW BatchAbort
          if (!origin.ok) { digest.push(origin.flagged); continue; }

          const verbChanges = await applyVerb(delta, ctx);
          const hoisted = hoistIndex(delta, verbChanges, indexEdit);
          changes = [...changes, ...hoisted.rest];
          indexEdit = hoisted.indexEdit;
          digest.push(...digestForApplied(delta, ctx.at));
        }
      } catch (e) {
        if (e instanceof BatchAbort) {
          // never half-apply: drop everything, fail-closed (runDream → no advance)
          return { changes: [], digest, aborted: true, abortReason: e.message };
        }
        throw e;
      }

      return { changes, ...(indexEdit ? { indexEdit } : {}), digest, aborted: false };
    },
  };
}

/** The digest entry(ies) for a delta that PASSED every gate and was applied.
 *  Paths mirror what applyVerb actually wrote (skill-seed→inbox/<date>/, quarantine→quarantine/<date>/). */
function digestForApplied(delta: ProposalDelta, at: Date): DigestEntry[] {
  const date = at.toISOString().slice(0, 10);
  switch (delta.verb) {
    case "new":
    case "supersede":
    case "retract":
      return [{ kind: "changed", path: changedPath(delta), verb: delta.verb, evidence: delta.evidence }];
    case "skill-seed":
      return [{ kind: "changed", path: `inbox/${date}/${delta.slug}.md`, verb: "new", evidence: delta.evidence }];
    case "quarantine":
      return [{ kind: "quarantined", path: `quarantine/${date}/${delta.slug}.md`, reason: delta.reason, evidence: delta.evidence }];
    case "flag":
      return [{ kind: "flagged", path: delta.path, tension: delta.tension, evidence: delta.evidence }];
    case "no-op":
      return [{ kind: "no-op", detail: delta.detail }];
  }
}

function changedPath(delta: Extract<ProposalDelta, { verb: "new" | "supersede" | "retract" }>): string {
  return delta.verb === "supersede" ? delta.newPath : delta.path;
}
